<script setup>
import {onMounted, ref, computed} from "vue";
import axios from "axios";
import {useToast} from 'vue-toastification';
import {storeToRefs} from 'pinia';
import {useAuthStore} from '@/stores/auth.store.js';

const toast = useToast();
const authStore = useAuthStore();
const {user} = storeToRefs(authStore);

const users = ref([]);
const loading = ref(false);
const search = ref('');

const showGrantModal = ref(false);
const grantTargetUser = ref(null);

// Mini directory browser inside the grant modal. Walks /api/files which
// already enforces the grantor's own scope, so a non-universal grantor
// physically cannot navigate above their grant root.
//
// Path semantics in this component mirror the storage convention:
// "" is the bucket root (universal grant); other paths are S3-native
// (no leading slash, trailing slash).
const browsePath = ref('');
const browseDirs = ref([]);
const browseLoading = ref(false);

const browseRoots = computed(() => {
  // Where the browser is allowed to start. For universal grantors that's
  // the bucket root (""); otherwise it's exactly their grants.
  return isUniversalGrantor.value ? [''] : myGrants.value;
});

const canGoUp = computed(() => {
  if (browsePath.value === '') return false;
  // Universal grantors can go anywhere; others can't navigate above one
  // of their explicit grants.
  if (isUniversalGrantor.value) return true;
  const parent = parentOf(browsePath.value);
  return myGrants.value.some(g => parent.startsWith(g));
});

function parentOf(p) {
  if (p === '') return '';
  const trimmed = p.endsWith('/') ? p.slice(0, -1) : p;
  const idx = trimmed.lastIndexOf('/');
  if (idx < 0) return '';
  return trimmed.slice(0, idx + 1);
}

const targetAlreadyHasBrowsePath = computed(() => {
  if (!grantTargetUser.value) return false;
  return grantTargetUser.value.grants.some(g => g.path === browsePath.value);
});

const canGrantBrowsePath = computed(() =>
  canManagePath(browsePath.value) && !targetAlreadyHasBrowsePath.value
);

const myGrants = computed(() => {
  // The grants the signed-in viewer holds, surfaced via customSession
  // in auth.ts. Used to bound what they can grant to others. Coming
  // from the session means it's still available even when the user
  // list is filtered (a directory_manager doesn't appear in their own
  // managed-users list, since they can only manage role=user accounts).
  // Storage convention: "" means universal grant.
  return user.value?.directories ?? [];
});

const isUniversalGrantor = computed(() => myGrants.value.includes(''));

// "" matches anything via startsWith, so no special case needed.
const canManagePath = (path) => myGrants.value.some(g => path.startsWith(g));

// Render an empty-string grant as "/" in the UI for human-friendliness
// even though it's stored as "" in the DB.
const displayPath = (path) => path === '' ? '/' : path;

const filteredUsers = computed(() => {
  if (!search.value) return users.value;
  const q = search.value.toLowerCase();
  return users.value.filter(u =>
    (u.name || '').toLowerCase().includes(q) ||
    (u.email || '').toLowerCase().includes(q)
  );
});

const fetchUsers = async () => {
  loading.value = true;
  try {
    const res = await axios.get(`${import.meta.env.VITE_API_BACKEND_URL}/api/directory-grants/users`, {
      withCredentials: true
    });
    users.value = res.data.users || [];
  } catch (err) {
    console.error(err);
    toast.error('Failed to load users');
  } finally {
    loading.value = false;
  }
};

const openGrantModal = (targetUser) => {
  grantTargetUser.value = targetUser;
  // Land at the first scope root. Universal grantor → "" (bucket root),
  // otherwise their first explicit grant.
  browsePath.value = browseRoots.value[0] ?? '';
  showGrantModal.value = true;
  loadBrowse();
};

const closeGrantModal = () => {
  showGrantModal.value = false;
  grantTargetUser.value = null;
  browseDirs.value = [];
};

const loadBrowse = async () => {
  browseLoading.value = true;
  try {
    const res = await axios.get(`${import.meta.env.VITE_API_BACKEND_URL}/api/files`, {
      params: { dir: browsePath.value },
      withCredentials: true,
    });
    browseDirs.value = res.data?.store?.dirs ?? [];
  } catch (err) {
    console.error(err);
    browseDirs.value = [];
    toast.error('Failed to load directory');
  } finally {
    browseLoading.value = false;
  }
};

const navigateInto = (dir) => {
  browsePath.value = dir.key.endsWith('/') ? dir.key : dir.key + '/';
  loadBrowse();
};

const navigateUp = () => {
  if (!canGoUp.value) return;
  browsePath.value = parentOf(browsePath.value);
  loadBrowse();
};

const switchToRoot = (root) => {
  browsePath.value = root;
  loadBrowse();
};

const grantBrowsePath = async () => {
  if (!canGrantBrowsePath.value) return;
  try {
    await axios.post(`${import.meta.env.VITE_API_BACKEND_URL}/api/directory-grants`, {
      user_id: grantTargetUser.value.id,
      path: browsePath.value,
    }, { withCredentials: true });
    toast.success(`Granted ${browsePath.value} to ${grantTargetUser.value.email}`);
    closeGrantModal();
    fetchUsers();
  } catch (err) {
    const msg = err.response?.data?.error || 'Failed to create grant';
    toast.error(msg);
  }
};

const revokeGrant = async (targetUser, grant) => {
  if (!canManagePath(grant.path)) {
    toast.error('You can only revoke grants within your own directory access');
    return;
  }
  if (!confirm(`Revoke ${grant.path} from ${targetUser.email}?`)) return;
  try {
    await axios.delete(`${import.meta.env.VITE_API_BACKEND_URL}/api/directory-grants/${grant.id}`, {
      withCredentials: true
    });
    toast.success('Grant revoked');
    fetchUsers();
  } catch (err) {
    const msg = err.response?.data?.error || 'Failed to revoke grant';
    toast.error(msg);
  }
};

onMounted(fetchUsers);
</script>

<template>
  <div class="directory-manager">
    <div class="d-flex flex-row justify-content-between align-items-center mb-3">
      <h1 class="h3 m-0">Directory access</h1>
      <div class="text-muted small" v-if="myGrants.length">
        Your scope:
        <span v-for="(g, i) in myGrants" :key="g">
          <code>{{ displayPath(g) }}</code><span v-if="i < myGrants.length - 1">, </span>
        </span>
      </div>
    </div>

    <div class="mb-3">
      <input v-model="search" class="form-control" placeholder="Search users by name or email" />
    </div>

    <div v-if="loading" class="text-muted">Loading…</div>

    <table v-else class="table align-middle">
      <thead>
        <tr>
          <th style="width:30%">User</th>
          <th style="width:10%">Role</th>
          <th>Granted directories</th>
          <th style="width:1%; white-space:nowrap;"></th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="u in filteredUsers" :key="u.id">
          <td>
            <div>{{ u.name || '—' }}</div>
            <div class="text-muted small">{{ u.email }}</div>
          </td>
          <td>
            <span class="badge bg-secondary">{{ u.role || '—' }}</span>
          </td>
          <td>
            <div v-if="!u.grants.length" class="text-muted small">No directory access</div>
            <div v-else class="d-flex flex-wrap gap-1">
              <span v-for="g in u.grants" :key="g.id"
                    class="badge"
                    :class="canManagePath(g.path) ? 'bg-light text-dark border' : 'bg-light text-muted border'"
                    :title="g.granted_by_email
                      ? `Granted by ${g.granted_by_email} at ${new Date(g.granted_at).toLocaleString()}`
                      : `Granted at ${new Date(g.granted_at).toLocaleString()} (system)`">
                <code>{{ displayPath(g.path) }}</code>
                <button v-if="canManagePath(g.path)"
                        type="button"
                        class="btn-close btn-close-sm ms-2"
                        aria-label="Revoke"
                        @click="revokeGrant(u, g)"></button>
              </span>
            </div>
          </td>
          <td>
            <button class="btn btn-sm btn-primary" @click="openGrantModal(u)">+ Grant</button>
          </td>
        </tr>
        <tr v-if="!filteredUsers.length">
          <td colspan="4" class="text-muted text-center py-4">No users match.</td>
        </tr>
      </tbody>
    </table>

    <!-- Grant modal -->
    <div v-if="showGrantModal" class="modal-backdrop-custom" @click.self="closeGrantModal">
      <div class="modal-card">
        <h5 class="mb-3">Grant directory to {{ grantTargetUser?.email }}</h5>

        <!-- Root picker only shown when the grantor has multiple scope roots. -->
        <div v-if="browseRoots.length > 1" class="mb-2">
          <label class="form-label small text-muted mb-1">Starting directory</label>
          <div class="d-flex flex-wrap gap-1">
            <button v-for="r in browseRoots"
                    :key="r"
                    type="button"
                    class="btn btn-sm"
                    :class="browsePath.startsWith(r) ? 'btn-primary' : 'btn-outline-secondary'"
                    @click="switchToRoot(r)">
              <code>{{ displayPath(r) }}</code>
            </button>
          </div>
        </div>

        <div class="browse-bar">
          <button type="button" class="btn btn-sm btn-outline-secondary"
                  :disabled="!canGoUp" @click="navigateUp">↑ Up</button>
          <code class="browse-current">{{ displayPath(browsePath) }}</code>
        </div>

        <ul class="list-group browse-list mb-3">
          <li v-if="browseLoading" class="list-group-item text-muted">Loading…</li>
          <li v-else-if="!browseDirs.length" class="list-group-item text-muted">No subdirectories</li>
          <li v-for="dir in browseDirs"
              :key="dir.key"
              class="list-group-item list-group-item-action browse-row"
              @click="navigateInto(dir)">
            📁 {{ dir.name }}
          </li>
        </ul>

        <div v-if="targetAlreadyHasBrowsePath" class="text-muted small mb-2">
          {{ grantTargetUser?.email }} already has access to <code>{{ displayPath(browsePath) }}</code>.
        </div>

        <div class="d-flex justify-content-between gap-2">
          <button class="btn btn-secondary" @click="closeGrantModal">Cancel</button>
          <button class="btn btn-primary"
                  :disabled="!canGrantBrowsePath"
                  @click="grantBrowsePath">
            Grant <code class="text-light">{{ displayPath(browsePath) }}</code>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.directory-manager {
  padding: 1rem 0;
}
code {
  font-size: 0.85em;
}
.modal-backdrop-custom {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
}
.modal-card {
  background: #fff;
  padding: 1.5rem;
  border-radius: 6px;
  width: min(560px, 90vw);
  box-shadow: 0 12px 30px rgba(0,0,0,0.2);
}
.btn-close-sm {
  width: 0.6rem;
  height: 0.6rem;
}
.browse-bar {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}
.browse-current {
  font-size: 0.9em;
  color: #555;
  word-break: break-all;
}
.browse-list {
  max-height: 280px;
  overflow-y: auto;
}
.browse-row {
  cursor: pointer;
}
.btn-primary code {
  background: rgba(255,255,255,0.2);
  padding: 0 0.25em;
  border-radius: 3px;
}
</style>
